import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useReveal from "../Components/useReveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faPhone,
  faHeart,
  faHandHoldingHeart,
  faPeopleGroup,
  faBrain,
  faArrowRight,
  faShieldHeart,
  faClock,
  faUserNurse,
  faHouseChimneyMedical,
  faCheck,
  faPlus,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import "../Styles/Home.css";
import heroImg from "../Images/image.jpg";
import aboutImg from "../Images/about1.jpg";
import svcPersonal from "../Images/service.jpg";
import svcCompanion from "../Images/service1.jpg";
import svcDementia from "../Images/service3.jpg";

const faqs = [
  {
    q: "What is nonmedical in-home care?",
    a: "Nonmedical in-home care supports daily living, such as personal care, companionship, meal preparation and light housekeeping, without providing clinical or medical treatment. It helps clients stay independent and comfortable in their own homes.",
  },
  {
    q: "How do you create a care plan?",
    a: "We start by understanding each client's unique needs, routines and preferences, then build a customized care plan around them. Plans are reviewed and adjusted as needs change.",
  },
  {
    q: "Are your caregivers trained and vetted?",
    a: "Yes. Our caregivers are empathetic, screened professionals who genuinely care about the well-being of our clients and are trained to deliver safe, compassionate support.",
  },
  {
    q: "What areas do you serve?",
    a: "We proudly serve our local community. Call us at 253-523-0098 and we'll be happy to confirm availability in your area.",
  },
];

function Home() {
  useReveal();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="home-section">
      <Navbar />

      {/* HERO */}
      <section className="hero">
        <div className="hero-photo">
          <img src={heroImg} alt="Caregiver supporting an elderly client" />
        </div>
        <span className="hero-bg-shape s1" />
        <span className="hero-bg-shape s2" />
        <span className="hero-bg-shape s3" />
        <div className="hero-inner hr-container">
          <div className="hero-content">
            <span className="hero-badge">
              <FontAwesomeIcon icon={faStar} /> Trusted Homecare Services
            </span>
            <h1>
              Compassionate <span>In-Home Care</span> You Can Rely On
            </h1>
            <p>
              At Harmony Rise Homecare, we enhance lives through dedicated
              nonmedical in-home care, fostering independence and improving
              quality of life for every client.
            </p>
            <div className="hero-actions">
              <Link to="/book-appointment" className="hr-btn hr-btn-primary hr-btn-lg">
                <FontAwesomeIcon icon={faCalendarCheck} /> Book An Appointment
              </Link>
              <a
                href="tel:2535230098"
                className="hr-btn hr-btn-ghost hr-btn-lg"
                style={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
              >
                <FontAwesomeIcon icon={faPhone} /> 253-523-0098
              </a>
            </div>
            <div className="hero-mini-stats">
              <div className="hero-mini">
                <strong>5★</strong>
                <span>Rated Service</span>
              </div>
              <div className="hero-mini">
                <strong>5+</strong>
                <span>Years Experience</span>
              </div>
              <div className="hero-mini">
                <strong>100%</strong>
                <span>Personalized Care</span>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-visual-card">
              <div className="hero-visual-emoji" role="img" aria-label="caring">
                🤝
              </div>
              <h3>Authentic Care</h3>
              <p>Prioritizing your well-being and independence, every day.</p>
            </div>
            <div className="hero-float hf1">
              <span className="hf-icon">
                <FontAwesomeIcon icon={faHeart} />
              </span>
              <div>
                Compassionate
                <small>Caregivers who care</small>
              </div>
            </div>
            <div className="hero-float hf2">
              <span className="hf-icon">
                <FontAwesomeIcon icon={faShieldHeart} />
              </span>
              <div>
                Safe &amp; Trusted
                <small>Vetted professionals</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="trust-bar">
        <div className="hr-container">
          <div className="trust-grid reveal">
            <div className="trust-item">
              <span className="ti-icon">
                <FontAwesomeIcon icon={faHandHoldingHeart} />
              </span>
              <div>
                <h4>Compassionate</h4>
                <span>Care with empathy</span>
              </div>
            </div>
            <div className="trust-item">
              <span className="ti-icon">
                <FontAwesomeIcon icon={faUserNurse} />
              </span>
              <div>
                <h4>Skilled Team</h4>
                <span>Trained caregivers</span>
              </div>
            </div>
            <div className="trust-item">
              <span className="ti-icon">
                <FontAwesomeIcon icon={faHouseChimneyMedical} />
              </span>
              <div>
                <h4>In-Home Comfort</h4>
                <span>Care where you live</span>
              </div>
            </div>
            <div className="trust-item">
              <span className="ti-icon">
                <FontAwesomeIcon icon={faClock} />
              </span>
              <div>
                <h4>Flexible Hours</h4>
                <span>6:00 AM – 10:30 PM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="hr-section">
        <div className="about-grid hr-container">
          <div className="about-visual reveal">
            <img
              src={aboutImg}
              alt="Caregiver helping with medication reminders"
              className="about-photo"
            />
            <span className="about-photo-frame" />
            <div className="about-badge">
              <span className="ab-num">5+</span>
              <span>
                Years of trusted
                <br />
                caregiving
              </span>
            </div>
          </div>
          <div className="about-body reveal">
            <span className="hr-eyebrow">Authentic Care</span>
            <h2 className="hr-heading">
              Prioritizing Your <span className="accent">Well-Being</span> and
              Independence
            </h2>
            <p className="hr-subheading">
              At Harmony Rise Homecare, we believe in enhancing lives through
              dedicated nonmedical in-home care. Our compassionate team is
              committed to fostering independence and improving quality of life
              for every client.
            </p>
            <div className="about-points">
              <div className="about-point">
                <span className="ap-check">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                <p>
                  <strong>Caring Beginnings</strong>
                  Care built on trust, dignity and genuine respect.
                </p>
              </div>
              <div className="about-point">
                <span className="ap-check">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                <p>
                  <strong>Personalized Support</strong>
                  Tailored to each individual's needs and preferences.
                </p>
              </div>
              <div className="about-point">
                <span className="ap-check">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                <p>
                  <strong>Holistic Approach</strong>
                  Physical, emotional and social well-being together.
                </p>
              </div>
            </div>
            <Link to="/about" className="hr-btn hr-btn-secondary">
              Learn More About Us <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="hr-section bg-cream" id="services">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">Quality Services</span>
            <h2 className="hr-heading">
              Comprehensive <span className="accent">Nonmedical Care</span>{" "}
              Tailored to You
            </h2>
            <p className="hr-subheading">
              Every service is designed around your comfort, independence and
              peace of mind.
            </p>
          </div>

          <div className="services-grid">
            {[
              {
                icon: faHandHoldingHeart,
                img: svcPersonal,
                title: "Personal Care Services",
                desc: "Support with daily activities while preserving dignity and independence.",
                points: [
                  "Assistance with Daily Activities",
                  "Personal Hygiene Support",
                  "Medication Reminders",
                ],
              },
              {
                icon: faPeopleGroup,
                img: svcCompanion,
                title: "Companion Care",
                desc: "Companionship and emotional support that eases loneliness.",
                points: [
                  "Engaging Social Activities",
                  "Transportation for Outings",
                  "Light Housekeeping",
                ],
              },
              {
                icon: faBrain,
                img: svcDementia,
                title: "Dementia Care",
                desc: "Specialized, safe support for individuals living with dementia.",
                points: [
                  "Cognitive Engagement",
                  "Safety Monitoring",
                  "Family Support Services",
                ],
              },
            ].map((s) => (
              <article className="service-card reveal" key={s.title}>
                <div className="service-media">
                  <img src={s.img} alt={s.title} />
                  <div className="service-icon">
                    <FontAwesomeIcon icon={s.icon} />
                  </div>
                </div>
                <div className="service-body">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <ul>
                    {s.points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                  <Link to="/services" className="service-link">
                    Learn more <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="hr-center" style={{ marginTop: "44px" }}>
            <Link to="/services" className="hr-btn hr-btn-secondary">
              View All Services <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY / APPROACH */}
      <section className="hr-section why">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">Our Unique Approach</span>
            <h2 className="hr-heading">
              Personalized Care That Goes Beyond Expectations
            </h2>
          </div>
          <div className="why-grid">
            {[
              {
                n: "01",
                t: "Customized Care Plans",
                d: "Every client receives a tailored care plan focused on their unique needs and preferences. We are driven by your individual journey.",
              },
              {
                n: "02",
                t: "Compassionate Professionals",
                d: "Our caregivers are not just skilled; they are empathetic professionals who genuinely care about the well-being of our clients.",
              },
              {
                n: "03",
                t: "Holistic Approach",
                d: "We address not only physical needs but also emotional and social aspects, ensuring a well-rounded support system.",
              },
            ].map((w) => (
              <div className="why-card reveal" key={w.n}>
                <div className="why-num">{w.n}</div>
                <h3>{w.t}</h3>
                <p>{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="hr-section-sm">
        <div className="hr-container">
          <div className="stats-strip reveal">
            <div className="stat-block">
              <h3>5★</h3>
              <span>Client Rated Service</span>
            </div>
            <div className="stat-block">
              <h3>5+</h3>
              <span>Years of Experience</span>
            </div>
            <div className="stat-block">
              <h3>4</h3>
              <span>Specialized Services</span>
            </div>
            <div className="stat-block">
              <h3>100%</h3>
              <span>Personalized Care</span>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="hr-section bg-mint" id="reviews">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">Happy Clients</span>
            <h2 className="hr-heading">What Our Clients Are Saying</h2>
          </div>
          <div className="testi-grid">
            <div className="testi-card reveal">
              <span className="testi-quote">“</span>
              <div className="testi-stars">★★★★★</div>
              <p>
                The care my mother received was exceptional. The team treated
                her like family, and I couldn't be more grateful for the support
                they provided.
              </p>
              <div className="testi-author">
                <span className="testi-avatar">A</span>
                <div>
                  <strong>Grateful Family Member</strong>
                  <span>Verified Client Review</span>
                </div>
              </div>
            </div>
            <div className="testi-card reveal">
              <span className="testi-quote">“</span>
              <div className="testi-stars">★★★★★</div>
              <p>
                Harmony Rise Homecare transformed our lives. The caregivers were
                professional and caring, making the journey much easier for us
                as a family.
              </p>
              <div className="testi-author">
                <span className="testi-avatar">H</span>
                <div>
                  <strong>Happy Family</strong>
                  <span>Verified Client Review</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="hr-section">
        <div className="hr-container">
          <div className="hr-center reveal">
            <span className="hr-eyebrow">Good to Know</span>
            <h2 className="hr-heading">Frequently Asked Questions</h2>
          </div>
          <div className="faq-wrap">
            {faqs.map((f, i) => (
              <div
                className={`faq-item ${openFaq === i ? "open" : ""}`}
                key={f.q}
              >
                <button
                  className="faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                >
                  {f.q}
                  <FontAwesomeIcon icon={faPlus} />
                </button>
                <div className="faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hr-section" style={{ paddingTop: 0 }}>
        <div className="hr-container">
          <div className="cta-band reveal">
            <h2>Ready to Care?</h2>
            <p>
              Connect with us for personalized support tailored to you and your
              family. Take the first step today.
            </p>
            <div className="cta-actions">
              <Link to="/book-appointment" className="hr-btn hr-btn-white hr-btn-lg">
                <FontAwesomeIcon icon={faCalendarCheck} /> Book An Appointment
              </Link>
              <a href="tel:2535230098" className="hr-btn hr-btn-ghost hr-btn-lg">
                <FontAwesomeIcon icon={faPhone} /> Call: 253-523-0098
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
